import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import {
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    LinearProgress
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { del, get, post, postFiles, put } from "../../config/axios";
import toast from "react-hot-toast";

const UploadVideo = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [videoTitle, setVideoTitle] = useState("");

    const handleGetVideos = async () => {
        try {
            setLoading(true);
            const data = await get(`/api/dashboard/website/videos`);
            const homeVideos = data?.filter((item) => item.section === "home");
            setVideos(homeVideos);
        } catch (error) {
            console.error("Error fetching videos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleGetVideos();
    }, []);

    const handleOpenModal = (video = null) => {
        setCurrentVideo(video);
        if (video) {
            setVideoTitle(video.title || "");
        } else {
            setVideoTitle("");
        }
        setVideoFile(null);
        setThumbnailFile(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setCurrentVideo(null);
        setVideoFile(null);
        setThumbnailFile(null);
    };

    const handleVideoFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                toast.error("Video file size should be less than 50MB");
                return;
            }
            setVideoFile(file);
        }
    };

    const handleThumbnailFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Thumbnail file size should be less than 5MB");
                return;
            }
            setThumbnailFile(file);
        }
    };

    const handleSubmit = async () => {
        if (!videoFile && !currentVideo) {
            return toast.error("Please select a video file");
        }

        try {
            setUploading(true);
            let videoUrl = currentVideo?.url;
            let thumbnailUrl = currentVideo?.thumbnail;

            // Upload video if new file selected
            if (videoFile) {
                const videoFormData = new FormData();
                videoFormData.append("file", videoFile);
                const { data: { url } } = await postFiles("/api/app/user/uploadImage", videoFormData);
                videoUrl = url;
            }

            // Upload thumbnail if new file selected
            if (thumbnailFile) {
                const thumbnailFormData = new FormData();
                thumbnailFormData.append("file", thumbnailFile);
                const { data: { url } } = await postFiles("/api/app/user/uploadImage", thumbnailFormData);
                thumbnailUrl = url;
            }

            if (currentVideo) {
                // Update existing video
                await put(`/api/dashboard/website/videos/${currentVideo._id}`, {
                    url: videoUrl,
                    thumbnail: thumbnailUrl,
                    title: videoTitle,
                    section: "home"
                });
                toast.success("Video updated successfully");
            } else {
                // Create new video
                await post(`/api/dashboard/website/videos`, {
                    url: videoUrl,
                    thumbnail: thumbnailUrl,
                    title: videoTitle,
                    section: "home"
                });
                toast.success("Video uploaded successfully");
            }

            handleCloseModal();
            await handleGetVideos();
        } catch (error) {
            console.error("Error uploading video:", error);
            toast.error("Failed to process video");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await del(`/api/dashboard/website/videos/${id}`);
            handleGetVideos();
        } catch (error) {
            console.error("Error deleting video:", error);
        }
    };

    return (
        <Layout>
            <div style={{ padding: "1rem" }}>
                <Typography variant="h5" paddingY={2}>
                    Manage Home Videos
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenModal()}
                    >
                        Add New Video
                    </Button>
                </Box>
                <TableContainer component={Paper} sx={{ maxWidth: "100%", maxHeight: 400, overflow: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>S. No.</b></TableCell>
                                <TableCell><b>Title</b></TableCell>
                                <TableCell><b>Thumbnail</b></TableCell>
                                <TableCell><b>Video Preview</b></TableCell>
                                <TableCell><b>Action</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : videos?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No videos available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                videos?.map((video, index) => (
                                    <TableRow key={video.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{video.title || "Untitled"}</TableCell>
                                        <TableCell>
                                            {video.thumbnail ? (
                                                <img
                                                    src={video.thumbnail}
                                                    alt="Thumbnail"
                                                    style={{ width: 100, height: 60, objectFit: 'cover', cursor: 'pointer' }}
                                                    onClick={() => window.open(video.thumbnail, '_blank')}
                                                />
                                            ) : (
                                                "No Thumbnail"
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <video width="150" controls>
                                                <source src={video.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenModal(video)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDelete(video._id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Add/Edit Video Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                    <DialogTitle style={{ display: "flex", justifyContent:"space-between" }}>
                        {currentVideo ? "Edit Video" : "Add New Video"}
                        <p style={{ fontSize:"12px", color:"blue" }}>Recommended thumbnail size: 1280 × 720 px for optimal quality.</p>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Video Title"
                                value={videoTitle}
                                onChange={(e) => setVideoTitle(e.target.value)}
                                sx={{ mb: 3 }}
                            />

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Video File {!currentVideo && "(required)"}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ mr: 2 }}
                                >
                                    {videoFile ? "Change Video" : "Select Video"}
                                    <input
                                        type="file"
                                        hidden
                                        accept="video/*"
                                        onChange={handleVideoFileSelect}
                                    />
                                </Button>
                                {videoFile && (
                                    <Typography variant="body2">
                                        {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(2)} MB)
                                    </Typography>
                                )}
                                {currentVideo?.url && !videoFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Current video will be kept
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Thumbnail Image (optional)
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ mr: 2 }}
                                >
                                    {thumbnailFile ? "Change Thumbnail" : "Select Thumbnail"}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleThumbnailFileSelect}
                                    />
                                </Button>
                                {thumbnailFile && (
                                    <Typography variant="body2">
                                        {thumbnailFile.name} ({(thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB)
                                    </Typography>
                                )}
                                {currentVideo?.thumbnail && !thumbnailFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Current thumbnail will be kept
                                    </Typography>
                                )}
                            </Box>

                            {uploading && <LinearProgress sx={{ mt: 2 }} />}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={uploading || (!videoFile && !currentVideo)}
                            variant="contained"
                        >
                            {uploading ? "Processing..." : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Layout>
    );
};

export default UploadVideo;